import {AfterViewInit, Component, OnDestroy, ViewChild} from "@angular/core";
import {Subject} from "rxjs";
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {catchError, takeUntil} from "rxjs/operators";
import {MatDialog} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {Router} from "@angular/router";
import {SiteModel} from "../../../../models/site.model";
import {NotificationService} from "../../../shared/services/notification.service";
import {SitesService} from "../../../shared/services/sites.service";
import {SiteModalComponent} from "./site-modal/site-modal.component";
import {ConfirmDeleteSiteModalComponent} from "./confirm-delete-site-modal/confirm-delete-site-modal.component";
import {routeNames} from "../../../../shared/route-names";

@Component({
    selector: "app-sites-management",
    templateUrl: "./sites-management.component.html",
    styleUrls: ["./sites-management.component.scss"],
})
export class SitesManagementComponent implements OnDestroy, AfterViewInit {
    @ViewChild(MatSort) private sort!: MatSort;

    private destroy$ = new Subject<void>();

    displayedColumns: string[] = [
        "label",
        "unityCount",
        "equipmentCount",
        "actions",
    ];

    dataSource = new MatTableDataSource<SiteModel>();
    selectedSite: SiteModel | null = null;

    constructor(
        private notificationService: NotificationService,
        private sitesService: SitesService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private router: Router,
    ) {
    }

    ngAfterViewInit() {
        this.dataSource.sort = this.sort;
        this.loadSites();
    }

    loadSites() {
        this.sitesService.getSites().pipe(
            takeUntil(this.destroy$),
            catchError((error) => {
                this.notificationService.error(
                    this.translate.instant("pages.sites_management.load_error"),
                );
                throw error;
            }),
        ).subscribe((response) => {
            this.dataSource.data = response.data;
        });
    }

    openSiteDialog(site?: SiteModel) {
        const dialogRef = this.dialog.open(SiteModalComponent, {
            width: '500px',
            height: 'auto',
            maxHeight: '90vh',
            disableClose: true,
            data: {site},
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    this.loadSites();
                }
            });
    }

    deleteSite(site: SiteModel) {
        const dialogRef = this.dialog.open(ConfirmDeleteSiteModalComponent, {
            width: '450px',
            data: {
                siteName: site.label,
                unityCount: site.unityCount,
                equipmentCount: site.equipmentCount
            },
            disableClose: true
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                if (result && site.id) {
                    this.sitesService.delete(site.id)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                            next: () => {
                                this.notificationService.success(
                                    this.translate.instant('pages.sites_management.site_deleted_successfully')
                                );
                                this.loadSites();
                            },
                            error: () => {
                                this.notificationService.error(
                                    this.translate.instant('pages.sites_management.site_deletion_failed')
                                );
                            }
                        });
                }
            });
    }

    navigateToImport(site: SiteModel) {
        if (site.id) {
            this.router.navigate([routeNames.administration, routeNames.equipmentImport, site.id]);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
